import {
  ContentBlock,
  Heading,
  Stack,
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProvider,
  Text,
} from 'braid-design-system';
import React from 'react';

import { PageWrapper } from '#src/components/PageWrapper/PageWrapper';
import { useLocalDemo } from '#src/context/LocalDemoContext';
import { logout } from '#src/utils/cognitoAuth';
import { exitLocalAdminSession } from '#src/utils/localAdmin';

import { AdminLogin } from './AdminLogin';
import { CommunityDirectoryAdmin } from './CommunityDirectory/CommunityDirectoryAdmin';
import { EventsAdmin } from './Events/EventsAdmin';
import { LocalAdminLogin } from './LocalAdminLogin';
import { AdminUsers } from './Users/AdminUsers';
import { useAdminSession } from './useAdminSession';

export const Admin = () => {
  const session = useAdminSession();
  const { isLocalDemo } = useLocalDemo();
  const [error, setError] = React.useState<string | null>(null);

  if (session.status === 'loading') {
    return (
      <PageWrapper>
        <ContentBlock>
          <Text>Loading…</Text>
        </ContentBlock>
      </PageWrapper>
    );
  }

  if (session.status === 'unconfigured') {
    return (
      <PageWrapper>
        <ContentBlock>
          <Stack space="medium">
            <Heading level="1">Admin</Heading>
            <Text>
              Admin is not configured yet. Deploy the infrastructure and update
              runtime-config.json with Cognito settings.
            </Text>
          </Stack>
        </ContentBlock>
      </PageWrapper>
    );
  }

  if (session.status === 'unauthenticated') {
    return (
      <PageWrapper>
        <ContentBlock>
          {isLocalDemo ? (
            <LocalAdminLogin
              onAuthenticated={() => {
                session.recheckAuth();
              }}
            />
          ) : (
            <AdminLogin
              onAuthenticated={() => {
                session.recheckAuth();
              }}
            />
          )}
        </ContentBlock>
      </PageWrapper>
    );
  }

  const { permissions } = session;

  const currentUser = {
    email: permissions.email,
    onLogout: () => {
      if (isLocalDemo) {
        exitLocalAdminSession();
        session.recheckAuth();
        return;
      }
      logout();
      session.recheckAuth();
    },
  };

  if (!permissions.canManageContent && !permissions.canManageUsers) {
    return (
      <PageWrapper currentUser={currentUser}>
        <ContentBlock>
          <Stack space="medium">
            <Heading level="1">Admin</Heading>
            <Text>
              Your account is signed in but does not have an admin role yet. Ask
              a super admin to assign you to the super-admin or content-admin
              group in Cognito.
            </Text>
          </Stack>
        </ContentBlock>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper currentUser={currentUser}>
      <ContentBlock>
        <TabsProvider>
          <Stack space="large">
            <Tabs label="Admin sections">
              {permissions.canManageContent ? (
                <>
                  <Tab item="directory">Community Directory</Tab>
                  <Tab item="events">Events</Tab>
                </>
              ) : null}
              {permissions.canManageUsers ? (
                <Tab item="users">Users</Tab>
              ) : null}
            </Tabs>

            {error ? <Text tone="critical">{error}</Text> : null}

            <TabPanels>
              {permissions.canManageContent ? (
                <>
                  <TabPanel item="directory">
                    <CommunityDirectoryAdmin onError={setError} />
                  </TabPanel>
                  <TabPanel item="events">
                    <EventsAdmin onError={setError} />
                  </TabPanel>
                </>
              ) : null}
              {permissions.canManageUsers ? (
                <TabPanel item="users">
                  <AdminUsers
                    currentUsername={
                      permissions.email ?? permissions.subject ?? null
                    }
                    onError={setError}
                  />
                </TabPanel>
              ) : null}
            </TabPanels>
          </Stack>
        </TabsProvider>
      </ContentBlock>
    </PageWrapper>
  );
};
